import { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUser } from '@/hooks/useUser';
import { isPublicPath } from '@/utils/publicRoutes';

/**
 * Redirects authenticated users without completed profile to onboarding page.
 * This runs globally and ensures users who logged in via OAuth complete profile
 * before accessing the app.
 */
export default function OnboardingGuard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, logout } = useAuth();
  const {
    isLoading: isProfileLoading,
    user: profileData,
    isUserNotFound: isProfileNotFound,
    isUserUnauthorized: isProfileUnauthorized,
    needsConsent,
    needsProfile,
  } = useUser();
  const lastNavRef = useRef<{ path: string; ts: number } | null>(null);

  const safeNavigate = useCallback(
    (target: string) => {
      if (location.pathname === target) return;
      const last = lastNavRef.current;
      if (last?.path === target && Date.now() - last.ts < 1000) return;
      lastNavRef.current = { path: target, ts: Date.now() };
      navigate(target);
    },
    [location.pathname, navigate]
  );

  useEffect(() => {
    if (isLoading || isProfileLoading) return;
    if (!isAuthenticated) return;

    // Do not enforce onboarding redirects while the user is browsing
    // public/info pages. Onboarding is enforced when entering app/protected areas.
    if (isPublicPath(location.pathname)) return;

    // 1) Unauthorized: session is effectively unusable for protected API calls.
    // Force a clean logout and send user to login.
    if (isProfileUnauthorized && location.pathname !== '/auth') {
      void (async () => {
        try {
          await logout({ useHostedUI: false });
        } catch {
          // ignore
        }
        safeNavigate('/auth');
      })();
      return;
    }

    // 2) Profile missing: decide route based on OAuth intent.
    if (isProfileNotFound) {
      let intent: string | null = null;
      try {
        intent = sessionStorage.getItem('oauth_intent');
      } catch {
        intent = null;
      }

      if (intent === 'login') {
        safeNavigate('/account-not-found');
        return;
      }

      // Default for register/unknown intent: proceed to onboarding.
      safeNavigate('/complete-profile');
      return;
    }

    // From here: service profile exists (profileData is non-null) or is in a
    // transient state, but it's not a confirmed 404.

    // If we can confirm the service profile exists, a stale intent from an
    // earlier OAuth attempt must not force /account-not-found.
    if (profileData) {
      try {
        sessionStorage.removeItem('oauth_intent');
      } catch {
        // ignore
      }
    }

    // 3) Enforce consent first.
    if (needsConsent) {
      safeNavigate('/consent');
      return;
    }

    // 4) Then enforce required profile fields.
    if (needsProfile) {
      safeNavigate('/complete-profile');
      return;
    }
  }, [
    isAuthenticated,
    isProfileNotFound,
    isProfileUnauthorized,
    needsConsent,
    needsProfile,
    isLoading,
    isProfileLoading,
    logout,
    navigate,
    location.pathname,
    safeNavigate,
    profileData,
  ]);

  return null;
}
